import cc from 'classcat';
import { useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { StopIcon } from '@i-ark/components';
import { useAudioPlayback, stopAudioPlayback, addAudioPlaybackStopListener, removeAudioPlaybackStopListener } from '@i-ark/hooks';
import { HotspotIconPosition } from '@i-ark/models';
import { preventDefault } from '@i-ark/utils';
import { useNotifier } from '@provider/notifier';
import { useSpeechSynthesisConfig, mapPollyVoice } from '@provider/speech-synthesis';
import { describeArcPath, loadPollyLibrary } from '@utils/common';
import { CSSRefTransition } from '@utils/react';
import { hotspotCls, speechStateCls, ICON_TRANSITION_DURATION, speechProgressCls, ICON_SIZE, PROGRESS_STROKE_WIDTH, hotspotIconCls } from './SpeechSynthesisHotspot.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const SpeechSynthesisHotspot = ({
  item
}) => {
  const notifier = useNotifier();
  const audioPlayback = useAudioPlayback();
  const speechConfig = useSpeechSynthesisConfig();
  const [loadingSpeechSynthesis, setLoadingSpeechSynthesis] = useState(false);
  const handleClick = async () => {
    const gPolly = await loadPollyLibrary();
    stopAudioPlayback();
    if (audioPlayback.progress !== undefined) {
      // if we were already playing audio, then don't restart it...
      return;
    }
    let autoPlayWhenReady = true;
    setLoadingSpeechSynthesis(true);

    // prevent automatic playback when audio begins playing
    const handleStopPlayback = () => {
      autoPlayWhenReady = false;
      setLoadingSpeechSynthesis(false);
      removeAudioPlaybackStopListener(handleStopPlayback);
    };
    addAudioPlaybackStopListener(handleStopPlayback);
    Promise.resolve(gPolly.synthesizeSpeech({
      text: item.text,
      speed: speechConfig.speed,
      voice: mapPollyVoice(speechConfig.languages?.[0] ?? "Danish", speechConfig.gender)
    })).then(async data => {
      removeAudioPlaybackStopListener(handleStopPlayback);
      if (autoPlayWhenReady) {
        return await audioPlayback.start(data.mp3Url);
      }
    }).then(() => {
      setLoadingSpeechSynthesis(false);
    }).catch(err => {
      console.warn(err);
      setLoadingSpeechSynthesis(false);
      notifier.notify({
        id: "i-ark.speech-synthesis.hotspot.error",
        type: "error",
        body: "Der opstod en fejl under oplæsning af teksten, vi beklager."
      });
    });
  };
  const handleMouseDown = preventDefault;
  const handleTouchStart = preventDefault;
  return /*#__PURE__*/jsx("button", {
    className: hotspotCls,
    onClick: handleClick,
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
    children: /*#__PURE__*/jsx("i", {
      className: cc([hotspotIconCls, {
        tl: item.style.icon.position === HotspotIconPosition.TOP_LEFT,
        tr: item.style.icon.position === HotspotIconPosition.TOP_RIGHT,
        bl: item.style.icon.position === HotspotIconPosition.BOTTOM_LEFT,
        br: item.style.icon.position === HotspotIconPosition.BOTTOM_RIGHT,
        bordered: !item.style.icon.enabled,
        loading: loadingSpeechSynthesis
      }]),
      children: /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: !loadingSpeechSynthesis && audioPlayback.progress !== undefined && /*#__PURE__*/jsx(CSSRefTransition, {
          timeout: ICON_TRANSITION_DURATION,
          classNames: speechStateCls,
          children: ref => /*#__PURE__*/jsxs("span", {
            ref: ref,
            className: speechStateCls,
            children: [/*#__PURE__*/jsx(StopIcon, {
              size: 10
            }), /*#__PURE__*/jsx("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: `0 0 ${ICON_SIZE} ${ICON_SIZE}`,
              className: speechProgressCls,
              children: /*#__PURE__*/jsx("path", {
                d: describeArcPath(ICON_SIZE, PROGRESS_STROKE_WIDTH, (audioPlayback.progress ?? 0) * 360)
              })
            })]
          })
        }, "stop")
      })
    })
  });
};

export { SpeechSynthesisHotspot };
//# sourceMappingURL=SpeechSynthesisHotspot.js.map
